import { packEnclose, packSiblings } from 'd3';

const TATA_DNA_MATCH_VARIANT = 'tata-dna-match';

const LAYOUT_REGION_AFFINITIES = [
    {
        id: 'claasz-westhoff',
        targetSubcladeId: 'alexandrien-frederique-westhoff',
        kind: 'adoptive',
        connector: true,
        strength: 0.32,
        matches: ({ type, variant, surnameKey }) => (
            type === 'claasz' || variant === 'claasz' || surnameKey === 'claasz'
        ),
    },
    {
        id: 'molukse-akolo',
        targetSubcladeId: 'akolo',
        kind: 'cultural',
        connector: false,
        strength: 0.24,
        matches: ({ visualVariant }) => visualVariant === 'molukse',
    },
    {
        id: 'java-batak-eyang-widya',
        targetSubcladeId: 'theresia-margareta',
        kind: 'dna',
        connector: false,
        strength: 0.24,
        matches: ({ variant, visualVariant }) => (
            variant === TATA_DNA_MATCH_VARIANT || visualVariant === TATA_DNA_MATCH_VARIANT
        ),
    },
];

export function normalizeClusterKey(value) {
    return (value || '')
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim();
}

/**
 * Describe a semantic layout relationship without turning it into a family
 * graph edge. Exact family/research matches take precedence over the broader
 * cultural and DNA categories.
 */
export function buildSubcladeSurnameAffinityTargets(subclades = []) {
    const candidates = new Map();

    for (const subclade of subclades) {
        if (!subclade?.id) continue;
        const key = getCanonicalClusterSurname(subclade.surnameKey || subclade.label).key;
        if (!key) continue;
        if (!candidates.has(key)) {
            candidates.set(key, subclade.id);
        } else if (candidates.get(key) !== subclade.id) {
            candidates.set(key, null);
        }
    }

    return new Map([...candidates].filter(([, targetId]) => targetId));
}

export function resolveLayoutRegionAffinity(region = {}, { subcladeSurnameTargets = new Map() } = {}) {
    const rawSurname = region.surnameKey || region.surname;
    const normalized = {
        ...region,
        surnameKey: getCanonicalClusterSurname(rawSurname).key || normalizeClusterKey(rawSurname),
    };
    const adoptiveDefinition = LAYOUT_REGION_AFFINITIES
        .find(item => item.kind === 'adoptive' && item.matches(normalized));
    if (adoptiveDefinition) {
        return {
            affinityId: adoptiveDefinition.id,
            affinityTarget: adoptiveDefinition.targetSubcladeId,
            affinityKind: adoptiveDefinition.kind,
            affinityConnector: adoptiveDefinition.connector,
            affinityStrength: adoptiveDefinition.strength,
        };
    }

    const isResearchIsland = ['single', 'surname'].includes(normalized.type)
        || ['island', 'surname-group'].includes(normalized.variant);
    const hasThematicVariant = ['molukse', TATA_DNA_MATCH_VARIANT]
        .includes(normalized.visualVariant);
    const surnameTarget = subcladeSurnameTargets instanceof Map
        ? subcladeSurnameTargets.get(normalized.surnameKey)
        : subcladeSurnameTargets?.[normalized.surnameKey];
    if (isResearchIsland && !hasThematicVariant && surnameTarget) {
        return {
            affinityId: `research-surname:${normalized.surnameKey}`,
            affinityTarget: surnameTarget,
            affinityKind: 'research',
            affinityConnector: true,
            affinityStrength: 0.28,
        };
    }

    const definition = LAYOUT_REGION_AFFINITIES.find(item => item.matches(normalized));
    if (!definition) return null;

    return {
        affinityId: definition.id,
        affinityTarget: definition.targetSubcladeId,
        affinityKind: definition.kind,
        affinityConnector: definition.connector,
        affinityStrength: definition.strength,
    };
}

function getRegionSurnameKeys(region = {}) {
    return new Set([
        region.surnameKey,
        ...(Array.isArray(region.surnameKeys) ? region.surnameKeys : []),
    ].map(normalizeClusterKey).filter(Boolean));
}

function buildSurnameAdjacency(surnameLinks = []) {
    const adjacency = new Map();
    const connect = (source, target) => {
        if (!source || !target || source === target) return;
        if (!adjacency.has(source)) adjacency.set(source, new Set());
        if (!adjacency.has(target)) adjacency.set(target, new Set());
        adjacency.get(source).add(target);
        adjacency.get(target).add(source);
    };

    for (const link of surnameLinks) {
        const source = normalizeClusterKey(Array.isArray(link) ? link[0] : link?.source);
        const target = normalizeClusterKey(Array.isArray(link) ? link[1] : link?.target);
        connect(source, target);
    }
    return adjacency;
}

function shortestSurnameDistance(sourceKeys, targetKeys, adjacency) {
    const targets = new Set(targetKeys);
    const queue = [...sourceKeys].map(key => ({ key, distance: 0 }));
    const visited = new Set(sourceKeys);

    for (let index = 0; index < queue.length; index += 1) {
        const current = queue[index];
        if (targets.has(current.key)) return current.distance;
        for (const neighbour of adjacency.get(current.key) || []) {
            if (visited.has(neighbour)) continue;
            visited.add(neighbour);
            queue.push({ key: neighbour, distance: current.distance + 1 });
        }
    }
    return Infinity;
}

/**
 * Build a deterministic visual family tree for Moluccan layout regions.
 * Akollo and Ruspana are roots attached to the Akolo main-network subclade.
 * Other regions attach through the closest already-connected region using
 * surname evidence found in their people, allowing indirect stepping stones.
 */
export function buildMolukseRegionConnectorTargets({
    regions = [],
    surnameLinks = [],
    anchorSurnameKeys = ['akollo', 'ruspana'],
    targetSubcladeId = 'akolo',
} = {}) {
    const molukseRegions = regions
        .filter(region => region?.id && region.visualVariant === 'molukse')
        .map(region => ({
            ...region,
            canonicalSurnameKey: normalizeClusterKey(region.surnameKey),
            surnameKeySet: getRegionSurnameKeys(region),
        }));
    const byId = new Map(molukseRegions.map(region => [region.id, region]));
    const anchors = anchorSurnameKeys
        .map(normalizeClusterKey)
        .map(key => molukseRegions.find(region => region.canonicalSurnameKey === key))
        .filter(Boolean);
    if (!anchors.length) return new Map();

    const targets = new Map();
    const connected = new Set();
    const routeDepth = new Map();
    const rootKeyByRegionId = new Map();
    const attachmentCount = new Map(anchors.map(anchor => [anchor.id, 0]));

    for (const anchor of anchors) {
        connected.add(anchor.id);
        routeDepth.set(anchor.id, 0);
        rootKeyByRegionId.set(anchor.id, anchor.canonicalSurnameKey);
        targets.set(anchor.id, {
            connectorTargetType: 'subclade',
            connectorTargetId: targetSubcladeId,
            connectorRootKey: anchor.canonicalSurnameKey,
            connectorEvidenceDistance: 0,
            connectorTraceable: true,
        });
    }

    // Prefer explicit anchor surnames in a region, e.g. Nanlohy containing a
    // Ruspana person, before considering broader surname-network distances.
    for (const region of molukseRegions) {
        if (connected.has(region.id)) continue;
        const directAnchor = anchors.find(anchor => (
            region.surnameKeySet.has(anchor.canonicalSurnameKey)
        ));
        if (!directAnchor) continue;
        connected.add(region.id);
        routeDepth.set(region.id, 1);
        rootKeyByRegionId.set(region.id, directAnchor.canonicalSurnameKey);
        attachmentCount.set(directAnchor.id, (attachmentCount.get(directAnchor.id) || 0) + 1);
        targets.set(region.id, {
            connectorTargetType: 'region',
            connectorTargetId: directAnchor.id,
            connectorRootKey: directAnchor.canonicalSurnameKey,
            connectorEvidenceDistance: 0,
            connectorTraceable: true,
        });
    }

    const adjacency = buildSurnameAdjacency(surnameLinks);
    const compareCandidates = (first, second) => {
        if (!second) return -1;
        if (first.distance !== second.distance) return first.distance - second.distance;
        if (first.depth !== second.depth) return first.depth - second.depth;
        return `${first.source.id}:${first.target.id}`
            .localeCompare(`${second.source.id}:${second.target.id}`);
    };

    while (connected.size < molukseRegions.length) {
        let best = null;
        for (const source of molukseRegions) {
            if (connected.has(source.id)) continue;
            for (const target of molukseRegions) {
                if (!connected.has(target.id)) continue;
                const candidate = {
                    source,
                    target,
                    distance: shortestSurnameDistance(
                        source.surnameKeySet,
                        target.surnameKeySet,
                        adjacency,
                    ),
                    depth: routeDepth.get(target.id) || 0,
                };
                if (compareCandidates(candidate, best) < 0) best = candidate;
            }
        }

        if (!best) break;
        let { target } = best;
        const traceable = Number.isFinite(best.distance);
        if (!traceable) {
            target = [...anchors].sort((first, second) => (
                (attachmentCount.get(first.id) || 0) - (attachmentCount.get(second.id) || 0)
                || first.id.localeCompare(second.id)
            ))[0];
        }

        const rootKey = rootKeyByRegionId.get(target.id) || target.canonicalSurnameKey;
        connected.add(best.source.id);
        routeDepth.set(best.source.id, (routeDepth.get(target.id) || 0) + 1);
        rootKeyByRegionId.set(best.source.id, rootKey);
        attachmentCount.set(target.id, (attachmentCount.get(target.id) || 0) + 1);
        targets.set(best.source.id, {
            connectorTargetType: 'region',
            connectorTargetId: target.id,
            connectorRootKey: rootKey,
            connectorEvidenceDistance: traceable ? best.distance : null,
            connectorTraceable: traceable,
        });
    }

    return new Map([...targets].filter(([regionId, target]) => (
        byId.has(regionId) && target.connectorTargetId
    )));
}

/**
 * Seed collision-aware macro regions around a related main-network subclade.
 * Related regions fan out perpendicular to the subclade direction; unrelated
 * research regions share one packed zone below the main network.
 */
export function buildLayoutRegionHomeTargets({
    regions = [],
    centerX = 0,
    centerY = 0,
    mainRadius = 260,
    spreadFactor = 1,
    targetAngles = new Map(),
    maximumAffinityBandSize = 5,
} = {}) {
    const homes = new Map();
    const goldenAngle = Math.PI * (3 - Math.sqrt(5));
    const getTargetAngle = targetId => (
        targetAngles instanceof Map ? targetAngles.get(targetId) : targetAngles?.[targetId]
    );
    const affinityBuckets = new Map();
    let fallbackIndex = 0;
    const researchRegions = [];

    for (const region of regions) {
        if (region.type === 'main') {
            homes.set(region.id, { x: centerX, y: centerY });
            continue;
        }

        const targetAngle = getTargetAngle(region.affinityTarget);
        if (region.affinityTarget && Number.isFinite(targetAngle)) {
            if (!affinityBuckets.has(region.affinityTarget)) {
                affinityBuckets.set(region.affinityTarget, { angle: targetAngle, regions: [] });
            }
            affinityBuckets.get(region.affinityTarget).regions.push(region);
            continue;
        }

        if (isUnlinkedResearchRegion(region, targetAngles)) {
            researchRegions.push(region);
            continue;
        }

        const angle = fallbackIndex * goldenAngle;
        const ring = Math.floor(fallbackIndex / 10);
        const distance = mainRadius + region.radius + (60 + ring * 88) * spreadFactor;
        homes.set(region.id, {
            x: centerX + distance * Math.cos(angle),
            y: centerY + distance * Math.sin(angle),
        });
        fallbackIndex += 1;
    }

    const bandSize = Math.max(1, maximumAffinityBandSize);
    for (const bucket of affinityBuckets.values()) {
        const ordered = [...bucket.regions].sort((a, b) => (
            b.radius - a.radius || String(a.id).localeCompare(String(b.id))
        ));
        const directionX = Math.cos(bucket.angle);
        const directionY = Math.sin(bucket.angle);
        const tangentX = -directionY;
        const tangentY = directionX;
        const gap = 34 * spreadFactor;
        let radialOffset = 0;

        for (let start = 0; start < ordered.length; start += bandSize) {
            const band = ordered.slice(start, start + bandSize);
            const totalWidth = band.reduce((sum, region) => sum + (region.radius * 2), 0)
                + Math.max(0, band.length - 1) * gap;
            let tangentCursor = -totalWidth / 2;
            let largestDiameter = 0;

            for (const region of band) {
                const tangentOffset = tangentCursor + region.radius;
                const baseClearance = region.affinityKind === 'adoptive'
                    ? 84
                    : region.affinityConnector ? 72 : 66;
                const distance = mainRadius + region.radius + (baseClearance * spreadFactor) + radialOffset
                    + Math.abs(tangentOffset) * 0.08;
                homes.set(region.id, {
                    x: centerX + directionX * distance + tangentX * tangentOffset,
                    y: centerY + directionY * distance + tangentY * tangentOffset,
                });
                tangentCursor += region.radius * 2 + gap;
                largestDiameter = Math.max(largestDiameter, region.radius * 2);
            }

            radialOffset += largestDiameter + gap;
        }
    }

    if (researchRegions.length) {
        const gap = 34 * spreadFactor;
        const circles = researchRegions.map(region => ({ id: region.id, r: region.radius + gap / 2 }))
            .sort((a, b) => b.r - a.r || String(a.id).localeCompare(String(b.id)));
        packSiblings(circles);
        const enclosure = packEnclose(circles);
        const zoneY = centerY + mainRadius + enclosure.r + 66 * spreadFactor;
        for (const circle of circles) homes.set(circle.id, {
            x: centerX + circle.x - enclosure.x,
            y: zoneY + circle.y - enclosure.y,
        });
    }
    return homes;
}

export function isUnlinkedResearchRegion(region, targetAngles = new Map()) {
    const angle = targetAngles instanceof Map ? targetAngles.get(region.affinityTarget) : targetAngles?.[region.affinityTarget];
    return region.type !== 'main' && region.type !== 'claasz' && region.variant !== 'claasz'
        && !['molukse', TATA_DNA_MATCH_VARIANT].includes(region.visualVariant || region.variant)
        && !(region.affinityTarget && Number.isFinite(angle));
}

/** Pack the research zone as one unit; move only whole regions out of collisions. */
export function packLayoutRegionTargets(options = {}) {
    const { regions = [], centerX = 0, centerY = 0, spreadFactor = 1, targetAngles = new Map() } = options;
    const homes = buildLayoutRegionHomeTargets(options);
    const research = regions.filter(region => isUnlinkedResearchRegion(region, targetAngles));
    const researchIds = new Set(research.map(region => region.id));
    const circles = regions.filter(region => !researchIds.has(region.id))
        .map(region => ({ ...region, ...homes.get(region.id) }))
        .sort((a, b) => a.type === 'main' ? -1 : b.type === 'main' ? 1 : 0);
    if (research.length) {
        const enclosure = packEnclose(research.map(region => ({ ...homes.get(region.id), r: region.radius + 17 * spreadFactor })));
        circles.push({ id: 'research-zone', x: enclosure.x, y: enclosure.y, radius: enclosure.r });
    }
    const placed = [];
    const targets = new Map();
    const gap = 34 * spreadFactor;
    for (const circle of circles) {
        let x = circle.x;
        let y = circle.y;
        if (circle.type !== 'main') {
            const dx = x - centerX;
            const dy = y - centerY;
            let distance = Math.hypot(dx, dy);
            const ux = distance ? dx / distance : 0;
            const uy = distance ? dy / distance : 1;
            // Each earlier circle excludes an interval along this ray. Moving
            // to interval ends is monotone and finishes after at most n passes.
            for (let pass = 0; pass <= placed.length; pass += 1) {
                let moved = false;
                for (const other of placed) {
                    const ox = other.x - centerX;
                    const oy = other.y - centerY;
                    const along = ox * ux + oy * uy;
                    const perpendicular = ox * uy - oy * ux;
                    const clearance = circle.radius + other.radius + gap;
                    const discriminant = clearance ** 2 - perpendicular ** 2;
                    if (discriminant < 0) continue;
                    const half = Math.sqrt(discriminant);
                    if (distance >= along - half && distance <= along + half) {
                        distance = along + half + 0.01;
                        moved = true;
                    }
                }
                if (!moved) break;
            }
            x = centerX + ux * distance;
            y = centerY + uy * distance;
        }
        placed.push({ ...circle, x, y });
        if (circle.id === 'research-zone') {
            for (const region of research) {
                const home = homes.get(region.id);
                targets.set(region.id, { x: home.x + x - circle.x, y: home.y + y - circle.y });
            }
        } else targets.set(circle.id, { x, y });
    }
    return targets;
}

export function getCanonicalClusterSurname(value, { isMolukseBranchComponent = false } = {}) {
    const key = normalizeClusterKey(value);
    const sharedMappings = {
        akolo: { key: 'akollo', label: 'Akollo' },
        akollo: { key: 'akollo', label: 'Akollo' },
        ngambe: { key: 'akollo', label: 'Akollo' },
        rumalatu: { key: 'akollo', label: 'Akollo' },
        corputty: { key: 'ruspana', label: 'Ruspana' },
        wemay: { key: 'ruspana', label: 'Ruspana' },
        rupp: { key: 'auliveld', label: 'Auliveld' },
        manuel: { key: 'auliveld', label: 'Auliveld' },
        mariano: { key: 'mollet', label: 'Mollet' },
        unglaub: { key: 'mollet', label: 'Mollet' },
        moerkerken: { key: 'mollet', label: 'Mollet' },
        mollet: { key: 'mollet', label: 'Mollet' },
        wevels: { key: 'mollet', label: 'Mollet' },
        brons: { key: 'jacobsz', label: 'Jacobsz' },
        jacobs: { key: 'jacobsz', label: 'Jacobsz' },
        jacobsz: { key: 'jacobsz', label: 'Jacobsz' },
        waldschmidt: { key: 'jacobsz', label: 'Jacobsz' },
        duman: { key: 'thumann', label: 'Thumann' },
        thumann: { key: 'thumann', label: 'Thumann' },
        neirincks: { key: 'neirinckx', label: 'Neirinckx' },
        neirinckx: { key: 'neirinckx', label: 'Neirinckx' },
        roespana: { key: 'ruspana', label: 'Ruspana' },
        roespanah: { key: 'ruspana', label: 'Ruspana' },
        ruspana: { key: 'ruspana', label: 'Ruspana' },
        ruspanah: { key: 'ruspana', label: 'Ruspana' },
        taniwel: { key: 'ruspana', label: 'Ruspana' },
        'la croix': { key: 'de la croix', label: 'de la Croix' },
        lacroix: { key: 'de la croix', label: 'de la Croix' },
        'de la croix': { key: 'de la croix', label: 'de la Croix' },
    };
    const molukseMappings = {
        rappa: { key: 'muskita', label: 'Muskita' },
        emmel: { key: 'sapulete', label: 'Sapulete' },
        'de laet': { key: 'pelupessy', label: 'Pelupessy' },
        flantua: { key: 'sohilait', label: 'Sohilait' },
    };

    return (isMolukseBranchComponent && molukseMappings[key])
        || sharedMappings[key]
        || { key, label: (value || '').toString().trim() };
}

export function buildAutomaticSurnameGroups(componentMetadata) {
    const byLabelVariant = new Map();

    for (const meta of componentMetadata) {
        const isSurnameIsland = meta.variant === 'island' && meta.surnameKey;
        const isTataDnaMatch = meta.variant === TATA_DNA_MATCH_VARIANT;
        if (!isSurnameIsland && !isTataDnaMatch) continue;

        const label = meta.surname || (isTataDnaMatch ? meta.fallbackLabel : '');
        const labelKey = meta.surnameKey || normalizeClusterKey(label);
        if (!labelKey || !label) continue;

        const groupKey = `${labelKey}:${meta.visualVariant}`;
        if (!byLabelVariant.has(groupKey)) {
            byLabelVariant.set(groupKey, {
                groupKey,
                surnameKey: labelKey,
                surname: label,
                visualVariant: meta.visualVariant,
                componentIds: [],
                peopleCount: 0,
            });
        }

        const group = byLabelVariant.get(groupKey);
        group.componentIds.push(meta.componentId);
        group.peopleCount += meta.size;
    }

    return [...byLabelVariant.values()]
        .filter(group => group.componentIds.length >= 2)
        .sort((a, b) => {
            if (b.componentIds.length !== a.componentIds.length) {
                return b.componentIds.length - a.componentIds.length;
            }
            if (b.peopleCount !== a.peopleCount) return b.peopleCount - a.peopleCount;
            return a.surname.localeCompare(b.surname, 'nl', { sensitivity: 'base' });
        });
}
